import React, { useState, useEffect } from 'react';
import { MessageCircle, ArrowRight } from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { useActiveProfile } from '../contexts/ActiveProfileContext';
import { sessionProfileParam } from '../utils/profileScope';

const HORA_MESSAGES = {
  Sun:     "Sun's clarity illuminates your path. Ready to explore your destiny?",
  Moon:    "Moon's intuition is heightened. Your chart has answers for you.",
  Mars:    "Mars energy is strong. The right question can change everything.",
  Mercury: "Mercury sharpens insight now. Ask what's been on your mind.",
  Jupiter: "Jupiter's wisdom is available. Deep guidance awaits.",
  Venus:   "Venus favors reflection today. Let's explore your relationships.",
  Saturn:  "Saturn's discipline brings clarity. Your karmic patterns reveal much.",
};

// B4: the preview is the last reading of the ACTIVE profile only. `profileId`
// overrides the app's active profile ("self" = the account owner).
const ChitraguptCTACard = ({ horaData, onStartSession, onUpgrade, isAuthenticated, hasBalance, resumeMode, profileId }) => {
  const activeProfile = useActiveProfile();
  const scopeId = profileId !== undefined ? profileId : (activeProfile?.profile_id || null);
  const [snippet, setSnippet]           = useState('');
  const [hasLastSession, setHasLastSession] = useState(false);
  const [snippetLoading, setSnippetLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setSnippet('');
    setHasLastSession(false);

    const fetchLast = async () => {
      setSnippetLoading(true);
      try {
        const token = localStorage.getItem('jyotish_session_token');
        const res = await fetch(`${getBackendUrl()}/api/chitragupt/session/last?${sessionProfileParam(scopeId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (cancelled) return; // a profile switch landed meanwhile
          if (data.found && data.session) {
            const summary = data.session.summary?.last_session_summary;
            let text = summary;
            if (!text) {
              const msgs = data.session.messages || [];
              const lastAI = [...msgs].reverse().find(m => m.role === 'assistant');
              text = lastAI?.content;
            }
            if (text) {
              setSnippet(text.slice(0, 60) + (text.length > 60 ? '…' : ''));
              setHasLastSession(true);
            }
          }
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setSnippetLoading(false);
      }
    };

    fetchLast();
    return () => { cancelled = true; };
  }, [isAuthenticated, scopeId]);

  const planet   = horaData?.current_hora?.planet || 'Sun';
  const symbol   = horaData?.current_hora?.symbol  || '☉';
  const message  = HORA_MESSAGES[planet] || HORA_MESSAGES.Sun;
  const ctaLabel = resumeMode ? 'Resume Session' : hasLastSession ? 'Continue Session' : 'Start Session';
  const onClick  = hasBalance ? onStartSession : onUpgrade;

  return (
    <div
      className="jp-card"
      style={{ padding: '14px 16px', position: 'relative', overflow: 'hidden' }}
      data-testid="chitragupt-cta-card"
    >
      {/* Ambient gold halo top-right */}
      <div style={{
        position: 'absolute', top: -20, right: -20, width: 90, height: 90,
        background: 'radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Identity row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {/* Avatar with online pulse */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: 'linear-gradient(135deg, rgba(201,168,76,0.20), rgba(201,168,76,0.08))',
            border: '1px solid var(--gold-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MessageCircle style={{ width: 15, height: 15, color: 'var(--gold-primary)' }} />
          </div>
          {/* Ping dot */}
          <div style={{
            position: 'absolute', bottom: -2, right: -2,
            width: 9, height: 9,
          }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%', background: '#4ADE80',
              animation: 'cg-ping 1.8s ease-out infinite',
            }} />
            <div style={{
              position: 'relative', width: 9, height: 9, borderRadius: '50%',
              background: '#4ADE80', border: '1.5px solid var(--bg-base)', zIndex: 1,
            }} />
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <p style={{ fontFamily: 'Cinzel, serif', fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
            ChitraGupt
          </p>
          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: '#4ADE80' }}>
            Online · {symbol} {planet} Hora
          </p>
        </div>
      </div>

      {/* Snippet or fallback message */}
      <div style={{
        background: 'rgba(255,255,255,0.04)', borderRadius: 7, padding: '8px 10px',
        border: '1px solid var(--card-border-subtle)', marginBottom: 10, minHeight: 42,
        display: 'flex', alignItems: 'center',
      }}>
        {snippetLoading ? (
          <div className="cs-skeleton" style={{ height: 10, borderRadius: 3, width: '80%' }} />
        ) : snippet ? (
          <p style={{
            fontFamily: 'Lora, serif', fontSize: 10,
            color: 'var(--text-secondary)', lineHeight: 1.6, fontStyle: 'italic',
          }}>
            "{snippet}"
          </p>
        ) : (
          <p style={{
            fontFamily: 'Lora, serif', fontSize: 10,
            color: 'var(--text-tertiary)', lineHeight: 1.5,
          }}>
            {message}
          </p>
        )}
      </div>

      {/* CTA */}
      <button
        onClick={onClick}
        className="jp-btn-primary"
        style={{ width: '100%', padding: '10px 14px', fontSize: 11, gap: 6 }}
        data-testid="chitragupt-cta-btn"
      >
        {ctaLabel}
        <ArrowRight style={{ width: 12, height: 12 }} />
      </button>
    </div>
  );
};

export default ChitraguptCTACard;
